import i18n from "i18next";
import { initReactI18next } from "react-i18next";

const LOCALE_KEY = "ieup_locale";

// Lazy per-file loaders (no `eager: true`) — the ~60 shipped locale JSON
// files total ~800KB, and eagerly bundling all of them into the entry chunk
// blew well past Vite's chunk-size warning. Only the active language (plus
// the "en" fallback) is loaded at startup; the rest load on demand when the
// user switches language in Settings.
const localeLoaders = import.meta.glob("./locales/*.json") as Record<
  string,
  () => Promise<{ default: Record<string, unknown> }>
>;

function langFromPath(path: string): string {
  return path.replace("./locales/", "").replace(".json", "");
}

const availableLangs = new Set(Object.keys(localeLoaders).map(langFromPath));

function detectLanguage(): string {
  // 1. User's saved preference
  const saved = localStorage.getItem(LOCALE_KEY);
  if (saved && availableLangs.has(saved)) return saved;

  // 2. Browser language (e.g. "fr-FR", "vi", "eu-AZ")
  const browserLang = navigator.language.toLowerCase().replace(/_/g, "-");
  if (availableLangs.has(browserLang)) return browserLang;

  // 3. Language-only match (e.g. "fr-FR" → "fr")
  const langOnly = browserLang.split("-")[0];
  if (availableLangs.has(langOnly)) return langOnly;

  return "en";
}

async function loadLocale(
  lang: string,
): Promise<Record<string, unknown> | null> {
  const loader = localeLoaders[`./locales/${lang}.json`];
  if (!loader) return null;
  const mod = await loader();
  return mod.default;
}

/**
 * Where a key missing in `lang` is looked up next: a regional dialect (eu-AZ, eu-BZ …) falls back to its base
 * language when we ship one (eu), then to en. A variant without a base bundle (es-CN, es-VL … — no plain es yet)
 * goes straight to en.
 */
export function fallbackChain(lang: string): string[] {
  const base = lang.split("-")[0];
  return base !== lang && base !== "en" && availableLangs.has(base) ? [base, "en"] : ["en"];
}

/** Loads and registers a language's resource bundle (and its fallback chain's) if they aren't already. */
export async function ensureLanguageLoaded(lang: string): Promise<void> {
  await Promise.all(
    [lang, ...fallbackChain(lang)].map(async (code) => {
      if (i18n.hasResourceBundle(code, "translation")) return;
      const data = await loadLocale(code);
      if (data) i18n.addResourceBundle(code, "translation", data);
    }),
  );
}

const initialLang = detectLanguage();

async function init(): Promise<void> {
  const codes = [...new Set([initialLang, ...fallbackChain(initialLang)])];
  const bundles = await Promise.all(codes.map(loadLocale));

  const resources: Record<string, { translation: Record<string, unknown> }> =
    {};
  codes.forEach((code, i) => {
    const data = bundles[i];
    if (data) resources[code] = { translation: data };
  });

  await i18n.use(initReactI18next).init({
    resources,
    lng: initialLang,
    fallbackLng: (code: string) => fallbackChain(code),
    interpolation: { escapeValue: false },
  });
}

const i18nReady = init();

export { LOCALE_KEY, i18nReady };
export default i18n;
